import { z } from 'zod'
import {
  Comparison,
  Id,
  Metres,
  MetresPerSecond,
  Milliseconds,
  VariableValue,
  Vec2,
} from './primitives.js'

/**
 * Every spatial trigger carries hysteresis and debounce. Raw GPS jitters by
 * metres even while standing still, so a bare "am I inside?" test machine-guns
 * on and off at any boundary. `exitMarginM` widens the area that must be left
 * before the trigger re-arms, and `debounceMs` requires the condition to hold.
 * These are field requirements, not polish, so they are defaulted rather than
 * optional.
 */
const spatialHysteresis = {
  exitMarginM: Metres.default(5),
  debounceMs: Milliseconds.default(2000),
}

const ZoneTrigger = z.object({
  type: z.literal('zone'),
  regionId: Id,
  transition: z.enum(['enter', 'exit']),
  ...spatialHysteresis,
})

const ProximityTrigger = z.object({
  type: z.literal('proximity'),
  target: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('point'), position: Vec2 }),
    z.object({ kind: z.literal('emitter'), emitterId: Id }),
  ]),
  radiusM: Metres,
  transition: z.enum(['enter', 'exit']),
  ...spatialHysteresis,
})

/** Lingering is intent: stay by the painting and hear more about it. */
const DwellTrigger = z.object({
  type: z.literal('dwell'),
  regionId: Id,
  durationMs: Milliseconds,
  ...spatialHysteresis,
})

const GateTrigger = z.object({
  type: z.literal('gate'),
  gateId: Id,
  direction: z.enum(['forward', 'backward', 'either']).default('forward'),
  debounceMs: Milliseconds.default(2000),
})

/**
 * Fire at a distance along a course. Because the listener is map-matched onto
 * the route first, this survives the lateral error that would make a proximity
 * circle miss entirely.
 */
const ChainageTrigger = z.object({
  type: z.literal('chainage'),
  courseId: Id,
  distanceM: Metres,
  direction: z.enum(['forward', 'backward', 'either']).default('forward'),
})

const SpeedTrigger = z.object({
  type: z.literal('speed'),
  comparison: z.enum(['above', 'below']),
  speedMps: MetresPerSecond,
  /** The threshold must hold this long, so one bad fix cannot trip it. */
  sustainedMs: Milliseconds.default(5000),
})

const MotionTrigger = z.object({
  type: z.literal('motion'),
  state: z.enum(['stopped', 'resumed']),
  afterMs: Milliseconds.default(60000),
})

const ElapsedTrigger = z.object({
  type: z.literal('elapsed'),
  sinceStartMs: Milliseconds,
})

/** Wall-clock scheduling. Always evaluated on the server. */
const WallClockTrigger = z.object({
  type: z.literal('wallClock'),
  cron: z.string().min(9).max(120),
  timezone: z.string().max(64).default('UTC'),
})

/** Fires when the named rules have fired in this order, optionally within a window. */
const SequenceTrigger = z.object({
  type: z.literal('sequence'),
  ruleIds: z.array(Id).min(2),
  withinMs: Milliseconds.optional(),
})

const VariableTrigger = z.object({
  type: z.literal('variable'),
  name: z.string().max(64),
  comparison: Comparison,
  value: VariableValue,
})

const RandomTrigger = z.object({
  type: z.literal('random'),
  /** Probability per evaluation second, so behaviour is independent of tick rate. */
  chancePerSecond: z.number().finite().min(0).max(1),
  minIntervalMs: Milliseconds.default(30000),
})

const RuleFiredTrigger = z.object({
  type: z.literal('ruleFired'),
  ruleId: Id,
})

const EmitterStateTrigger = z.object({
  type: z.literal('emitterState'),
  emitterId: Id,
  state: z.enum(['started', 'ended']),
})

/** An operator pressing a soundboard button. Always server-scoped. */
const OperatorCueTrigger = z.object({
  type: z.literal('operatorCue'),
  cueId: Id,
})

/** Lets a world react gracefully when positioning becomes untrustworthy. */
const AccuracyTrigger = z.object({
  type: z.literal('accuracy'),
  state: z.enum(['degraded', 'restored']),
})

const SessionTrigger = z.object({
  type: z.literal('session'),
  event: z.enum(['start', 'end']),
})

export const Trigger = z.discriminatedUnion('type', [
  ZoneTrigger,
  ProximityTrigger,
  DwellTrigger,
  GateTrigger,
  ChainageTrigger,
  SpeedTrigger,
  MotionTrigger,
  ElapsedTrigger,
  WallClockTrigger,
  SequenceTrigger,
  VariableTrigger,
  RandomTrigger,
  RuleFiredTrigger,
  EmitterStateTrigger,
  OperatorCueTrigger,
  AccuracyTrigger,
  SessionTrigger,
])
export type Trigger = z.infer<typeof Trigger>
export type TriggerType = Trigger['type']

/**
 * The trigger names as data, for schemas that reference a type without
 * carrying a whole trigger (events, diagnostics, studio pickers). A unit test
 * asserts this list stays in step with the union above.
 */
export const TRIGGER_TYPES = [
  'zone',
  'proximity',
  'dwell',
  'gate',
  'chainage',
  'speed',
  'motion',
  'elapsed',
  'wallClock',
  'sequence',
  'variable',
  'random',
  'ruleFired',
  'emitterState',
  'operatorCue',
  'accuracy',
  'session',
] as const satisfies readonly TriggerType[]

export const TriggerTypeEnum = z.enum(TRIGGER_TYPES)

/**
 * Triggers that cannot be decided on a listener's device: they depend on wall
 * clock, on an operator, or on state shared across participants. A rule using
 * any of these is forced to session scope at publish time.
 */
export const SERVER_ONLY_TRIGGER_TYPES = ['wallClock', 'operatorCue'] as const satisfies readonly TriggerType[]
